import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { 
  HH_SIZE_PATTERN, 
  ID_NUMBER_PATTERN, 
  NUMBER_DECIMAL_PATTERN, 
  SOCIAL_SECURITY_PATTERN, 
  ZIP_CODE_PATTERN,
  TEN_DIGIT_NUMBER_PATTERN
} from './validation-patterns';

/**
 * Validator for US ZIP codes (5 digits only).
 * Accepts format: 12345
 * 
 * @returns ValidatorFn that validates ZIP code format
 * 
 * @example
 * ```typescript
 * this.form = this.fb.group({
 *   zipCode: ['', zipCodeValidator()]
 * });
 * ```
 */
export function zipCodeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const zipPattern = ZIP_CODE_PATTERN;
    const isValid = zipPattern.test(control.value);

    return isValid ? null : { invalidZipCode: { value: control.value } };
  };
}

/**
 * Validator for US Social Security Numbers.
 * Accepts formats: 123-45-6789, 123456789
 * 
 * @returns ValidatorFn that validates SSN format
 * 
 * @example
 * ```typescript
 * this.form = this.fb.group({
 *   ssn: ['', ssnValidator()]
 * });
 * ```
 */
export function ssnValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const ssnPattern = SOCIAL_SECURITY_PATTERN;
    const isValid = ssnPattern.test(control.value);

    return isValid ? null : { invalidSSN: { value: control.value } };
  };
}

/** Last 4 SSN digits, including leading zeros. Accepts a full SSN or a last-4 value. */
export function ssnLast4Digits(value: unknown): string {
  if (value == null || String(value).trim() === '') {
    return '';
  }
  const digits = String(value).replace(/\D/g, '');
  if (!digits) {
    return '';
  }
  return digits.length > 4 ? digits.slice(-4) : digits;
}

/**
 * Validator for the last 4 digits of a Social Security Number.
 * Requires exactly 4 numeric digits. Pads a valid last-4 value with a fixed prefix
 * so reserved last-4 values (such as 0000) are still rejected via {@link ssnValidator}.
 *
 * @returns ValidatorFn that validates SSN last 4 digits format
 *
 * @example
 * ```typescript
 * this.form = this.fb.group({
 *   ssnLast4: ['', ssnLast4Validator()]
 * });
 * ```
 */
export function ssnLast4Validator(): ValidatorFn {
  const validateFullSsn = ssnValidator();

  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const value = String(control.value).trim();
    if (!/^\d{4}$/.test(value)) {
      return { invalidSSN: { value: control.value } };
    }

    const errors = validateFullSsn({ value: `12345${value}` } as AbstractControl);

    return errors ? { invalidSSN: { value: control.value } } : null;
  };
}

/**
 * Validator for URLs.
 * Validates http and https URLs. Requires valid protocol.
 * 
 * @returns ValidatorFn that validates URL format
 * 
 * @example
 * ```typescript
 * this.form = this.fb.group({
 *   website: ['', urlValidator()]
 * });
 * ```
 */
export function urlValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    try {
      const url = new URL(control.value);
      return (url.protocol === 'http:' || url.protocol === 'https:') 
        ? null 
        : { invalidUrl: { value: control.value } };
    } catch {
      return { invalidUrl: { value: control.value } };
    }
  };
}

/**
 * Validator that checks if a numeric value is within a specified range.
 * 
 * @param min - Minimum allowed value (inclusive)
 * @param max - Maximum allowed value (inclusive)
 * @returns ValidatorFn that validates numeric range
 * 
 * @example
 * ```typescript
 * this.form = this.fb.group({
 *   age: ['', [Validators.required, numericRangeValidator(0, 120)]]
 * });
 * ```
 */
export function numericRangeValidator(min: number, max: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value && control.value !== 0) {
      return null;
    }

    const value = Number(control.value);
    
    if (Number.isNaN(value)) {
      return { notANumber: true };
    }

    if (value < min) {
      return { belowMinimum: { min, actual: value } };
    }

    if (value > max) {
      return { aboveMaximum: { max, actual: value } };
    }

    return null;
  };
}

/**
 * Validator that ensures a string contains only alphanumeric characters.
 * Optionally allows spaces and specific special characters.
 * 
 * @param allowSpaces - Whether to allow spaces (default: false)
 * @param allowedSpecialChars - String of special characters to allow (default: '')
 * @returns ValidatorFn that validates alphanumeric content
 * 
 * @example
 * ```typescript
 * // Only letters and numbers
 * this.form = this.fb.group({
 *   username: ['', alphanumericValidator()]
 * });
 * 
 * // Allow spaces and hyphens
 * this.form = this.fb.group({
 *   displayName: ['', alphanumericValidator(true, '-')]
 * });
 * ```
 */
export function alphanumericValidator(allowSpaces = false, allowedSpecialChars = ''): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const specialChars = allowedSpecialChars.replaceAll(/[-[\]{}()*+?.,\\^$|#]/g, String.raw`\$&`);
    const spacePattern = allowSpaces ? String.raw`\s` : '';
    const pattern = new RegExp(`^[a-zA-Z0-9${spacePattern}${specialChars}]+$`);

    const isValid = pattern.test(control.value);

    return isValid ? null : { invalidAlphanumeric: { value: control.value } };
  };
}

/**
 * Validator that requires at least one checkbox to be checked in a FormArray.
 * 
 * @returns ValidatorFn that validates at least one item is selected
 * 
 * @example
 * ```typescript
 * this.form = this.fb.group({
 *   interests: this.fb.array([], atLeastOneCheckedValidator())
 * });
 * ```
 */
export function atLeastOneCheckedValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value || !Array.isArray(control.value)) {
      return { noneChecked: true };
    }

    const hasChecked = control.value.some((value: unknown) => value === true || value === 'true');
    
    return hasChecked ? null : { noneChecked: true };
  };
}


/**
 * Validator function for Federal Employer Identification Number (FEIN) input.
 *
 * Validates that the control value contains exactly 9 numeric digits (`^\d{9}$`).
 * If the control value is empty or not provided, validation is skipped and `null` is returned.
 *
 * @returns A `ValidatorFn` that returns:
 * - `null` when the value is empty or matches a valid 9-digit FEIN.
 * - `ValidationErrors` with the `invalidFEIN` key and the original value when invalid.
 */
export function feinValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const feinPattern = /^\d{9}$/;
    const isValid = feinPattern.test(control.value);

    return isValid ? null : { invalidFEIN: { value: control.value } };
  };
}

/**
 * Validator for PowerSchool/UID values.
 * Accepts numbers only.
 *
 * @returns ValidatorFn that validates numeric-only UID format
 */
export function numberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const uidValue = String(control.value);
    const uidPattern = ID_NUMBER_PATTERN;
    const isValid = uidPattern.test(uidValue);

    return isValid ? null : { invalidNumber: { value: control.value } };
  };
}

/**
 * Validator that accepts only exactly 10 numeric digits.
 *
 * @returns ValidatorFn that validates a strict 10-digit number format
 */
export function tenDigitNumberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const value = String(control.value).trim();
    const tenDigitPattern = TEN_DIGIT_NUMBER_PATTERN;
    const isValid = tenDigitPattern.test(value);

    return isValid ? null : { invalidTenDigitNumber: { value: control.value } };
  };
}

/**
 * Validator for household size.
 * Accepts numeric values with up to 2 digits (0-99).
 *
 * @returns ValidatorFn that validates household size format
 */
export function householdSizeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value && control.value !== 0) {
      return null;
    }

    const householdSizeValue = String(control.value).trim();
    const householdSizePattern = HH_SIZE_PATTERN;
    const isValid = householdSizePattern.test(householdSizeValue);

    return isValid ? null : { invalidHHSize: { value: control.value } };
  };
}

/**
 * Validator that allows only non-negative whole/decimal numbers.
 *
 * Rules:
 * - Allows: 0, 12, 12.34
 * - Rejects: negative values (including -0), explicit plus prefix (+1, +0), and non-numeric input
 *
 * @returns ValidatorFn that validates unsigned numeric/decimal input
 */
export function numericOrDecimalValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value && control.value !== 0) {
      return null;
    }

    const rawValue = typeof control.value === 'string' ? control.value : String(control.value);
    const value = rawValue.trim();

    // Explicit sign prefixes are not allowed (e.g., +1, +0, -0)
    // Includes common unicode plus/minus variants as well.
    if (/^[+\-−＋]/.test(value)) {
      return { invalidNumberOrDecimal: { value: control.value } };
    }

    const numberOrDecimalPattern = NUMBER_DECIMAL_PATTERN;
    const isValid = numberOrDecimalPattern.test(value);

    return isValid ? null : { invalidNumberOrDecimal: { value: control.value } };
  };
}

/**
 * Validator for address/city fields.
 * Rejects values that contain only whitespace.
 *
 * @returns ValidatorFn that validates non-whitespace text input
 */
export function noWhitespaceValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (typeof control.value !== 'string' || control.value === '') {
      return null;
    }

    return control.value.trim().length === 0
      ? { whitespaceOnly: { value: control.value } }
      : null;
  };
}

/**
 * Validator for SSN display/edit fields that may contain:
 * - full SSN digits: 123456789
 * - formatted SSN: 123-45-6789
 * - masked display value: XXX-XX-6789 or ***-**-6789
 * - last-4 only: 6789
 *
 * @returns ValidatorFn that validates SSN display/edit formats
 */
export function ssnDisplayValidator(): ValidatorFn {
  const maskedDisplayPattern = /^(X{3}|\*{3})-(X{2}|\*{2})-\d{4}$/i;
  const validateFullSsn = ssnValidator();

  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const raw = String(control.value).trim();
    if (!raw) {
      return null;
    }

    if (maskedDisplayPattern.test(raw)) {
      return null;
    }

    const digits = raw.replace(/\D/g, '');

    if (digits.length === 4) {
      // Reuse reserved-value checks from ssnValidator by padding a valid prefix.
      const errors = validateFullSsn({ value: `12345${digits}` } as AbstractControl);
      return errors ? { invalidSSN: { value: control.value } } : null;
    }

    if (digits.length === 9) {
      const normalized = raw.includes('-')
        ? raw
        : `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
      return validateFullSsn({ value: normalized } as AbstractControl);
    }

    return { invalidSSN: { value: control.value } };
  };
}